import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../../../utils/api';
import { useToast, ToastHost } from '../../../components/portal/Toast';
import {
  RiTaskLine,
  RiAddLine,
  RiSearchLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCloseLine,
  RiSaveLine,
  RiRefreshLine,
  RiUserLine,
  RiCalendarLine,
  RiTimeLine,
  RiDraggable,
  RiPlayCircleLine,
  RiCheckboxCircleLine,
} from 'react-icons/ri';

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUSES   = ['todo', 'in_progress', 'completed'];

const EMPTY_FORM = {
  title: '', description: '', assignedTo: '',
  dueDate: '', status: 'todo',
};

const STATUS_LABEL = { todo: 'To Do', in_progress: 'In Progress', completed: 'Completed' };

const KANBAN_COLUMNS = [
  { key: 'todo',        label: 'To Do',       icon: RiTimeLine,           color: '#9CA3AF', accent: 'rgba(156,163,175,0.4)' },
  { key: 'in_progress', label: 'In Progress', icon: RiPlayCircleLine,     color: '#0984e3', accent: 'rgba(9,132,227,0.5)'  },
  { key: 'completed',   label: 'Completed',   icon: RiCheckboxCircleLine, color: '#10b981', accent: 'rgba(16,185,129,0.5)' },
];

function parse(res) {
  const d = res.data;
  return Array.isArray(d) ? d : d?.tasks || d?.data || [];
}

function formatDue(raw) {
  if (!raw) return '—';
  return new Date(raw).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}


// ── Modal ─────────────────────────────────────────────────────────────────────
function Modal({ title, subtitle, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-[#1F2937]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-[#E5E7EB] rounded-2xl w-full max-w-lg shadow-[0_24px_60px_rgba(15,23,42,0.15)] max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between px-6 pt-6 pb-5 border-b border-[#F3F4F6] sticky top-0 bg-white rounded-t-2xl">
          <div>
            <h3 className="text-[#1F2937] font-bold text-lg">{title}</h3>
            {subtitle && <p className="text-[#9CA3AF] text-sm mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#1F2937] cursor-pointer transition-colors p-1 rounded-lg hover:bg-[#F3F4F6]">
            <RiCloseLine size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// ── Task form (assign + edit) ─────────────────────────────────────────────────
function TaskForm({ initial, employees, onSave, onCancel, isEdit }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onSave(form);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'input-field';
  const labelCls = 'block text-xs font-semibold text-[#6B7280] mb-1.5 uppercase tracking-wide';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>
      )}

      {/* Assign to employee */}
      <div>
        <label className={labelCls} htmlFor="assignedTo">
          Assign To <span className="text-red-500 normal-case">*</span>
        </label>
        <select
          id="assignedTo"
          required
          value={form.assignedTo}
          onChange={(e) => set('assignedTo', e.target.value)}
          className={`${inputCls} cursor-pointer`}
        >
          <option value="">— Select Employee —</option>
          {employees.map((u) => (
            <option key={u._id || u.id} value={u._id || u.id}>
              {u.name || u.username || u.email_id} {u.position ? `(${u.position})` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Title */}
      <div>
        <label className={labelCls} htmlFor="taskTitle">
          Task Title <span className="text-red-500 normal-case">*</span>
        </label>
        <input
          id="taskTitle"
          type="text"
          required
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder="e.g. Fix login bug on mobile"
          className={inputCls}
        />
      </div>

      {/* Description */}
      <div>
        <label className={labelCls} htmlFor="taskDesc">Description</label>
        <textarea
          id="taskDesc"
          rows={3}
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Additional details, requirements, or context…"
          className={`${inputCls} resize-none`}
        />
      </div>

      {/* Due Date */}
      <div>
        <label className={labelCls} htmlFor="taskDue">Due Date <span className="text-[#9CA3AF] normal-case font-normal tracking-normal">(optional)</span></label>
        <input
          id="taskDue"
          type="date"
          value={form.dueDate}
          onChange={(e) => set('dueDate', e.target.value)}
          className={inputCls}
        />
      </div>

      {/* Status (edit only) */}
      {isEdit && (
        <div>
          <label className={labelCls} htmlFor="taskStatus">Status</label>
          <select
            id="taskStatus"
            value={form.status}
            onChange={(e) => set('status', e.target.value)}
            className={`${inputCls} cursor-pointer`}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
            ))}
          </select>
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button type="button" onClick={onCancel} className="btn btn-outline flex-1 justify-center">
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary flex-1 justify-center disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading
            ? <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
            : <RiSaveLine size={15} />}
          {isEdit ? 'Save Changes' : 'Assign Task'}
        </button>
      </div>
    </form>
  );
}

// ── Delete confirm ─────────────────────────────────────────────────────────────
function DeleteConfirm({ task, onConfirm, onCancel, loading }) {
  return (
    <div className="text-center space-y-5">
      <div className="w-16 h-16 bg-red-50 border border-red-100 rounded-2xl flex items-center justify-center mx-auto">
        <RiDeleteBinLine size={26} className="text-red-500" />
      </div>
      <div>
        <p className="text-[#1F2937] font-bold text-lg">Delete task?</p>
        <p className="text-[#6B7280] text-sm mt-1 font-medium">"{task.title}"</p>
        <p className="text-[#9CA3AF] text-sm mt-1.5">This action cannot be undone.</p>
      </div>
      <div className="flex gap-3">
        <button onClick={onCancel} className="btn btn-outline flex-1 justify-center">Cancel</button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 btn justify-center bg-red-500 hover:bg-red-600 text-white border-transparent disabled:opacity-60"
        >
          {loading && <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />}
          Delete Task
        </button>
      </div>
    </div>
  );
}
// ── Main ───────────────────────────────────────────────────────────────────────
export default function AdminTasks() {
  const [tasks,     setTasks]     = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [modal,    setModal]    = useState(null); // null | 'assign' | 'edit' | 'delete'
  const [selected, setSelected] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { toast, showToast, clearToast } = useToast();

  // Kanban state: which employee's board are we viewing?
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
  const [draggedId,           setDraggedId]           = useState(null);
  const [hoverColumn,         setHoverColumn]         = useState(null);
  const [updatingId,          setUpdatingId]          = useState(null);

  // Debounce search by 300ms before firing the API call
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch all tasks (limit=100) so we can render a complete Kanban per user.
  const loadTasks = useCallback(() => {
    setLoading(true);
    setError('');
    const params = { page: 1, limit: 100 };
    if (debouncedSearch) params.search = debouncedSearch;
    api.get('/tasks', { params })
      .then((res) => setTasks(parse(res)))
      .catch(() => setError('Failed to load tasks.'))
      .finally(() => setLoading(false));
  }, [debouncedSearch]);

  // ── Drag-and-drop handlers ────────────────────────────────────────────────
  const onDragStart = (e, taskId) => {
    setDraggedId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', taskId); } catch (_) {}
  };
  const onDragEnd = () => { setDraggedId(null); setHoverColumn(null); };
  const onDragOver = (e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoverColumn !== colKey) setHoverColumn(colKey);
  };
  const onDragLeave = (e, colKey) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (hoverColumn === colKey) setHoverColumn(null);
  };
  const onDrop = async (e, newStatus) => {
    e.preventDefault();
    setHoverColumn(null);
    const taskId = draggedId || e.dataTransfer.getData('text/plain');
    setDraggedId(null);
    if (!taskId) return;
    const task = tasks.find((t) => (t._id || t.id) === taskId);
    if (!task || task.status === newStatus) return;
    const oldStatus = task.status;
    setTasks((prev) => prev.map((t) => (t._id || t.id) === taskId ? { ...t, status: newStatus } : t));
    setUpdatingId(taskId);
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      showToast(`Task moved to ${STATUS_LABEL[newStatus]}.`, 'success');
    } catch (err) {
      setTasks((prev) => prev.map((t) => (t._id || t.id) === taskId ? { ...t, status: oldStatus } : t));
      showToast(err.response?.data?.message || 'Failed to update status.', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  // Load tasks + employee list on mount
  useEffect(() => {
    loadTasks();
    api.get('/users/employees')
      .then((res) => {
        const all = res.data?.users || res.data?.employees || res.data?.data || res.data || [];
        setEmployees(all);
      })
      .catch(() => {});
  }, [loadTasks]);

  // ── CRUD handlers ────────────────────────────────────────────────────────────
  // Transform camelCase form fields to snake_case expected by backend
  const toPayload = (form) => {
    const payload = {
      title:       form.title,
      description: form.description,
      assigned_to: form.assignedTo,
      status:      form.status,
    };
    if (form.dueDate) payload.due_date = form.dueDate;
    return payload;
  };

  const handleAssign = async (form) => {
    await api.post('/tasks', toPayload(form));
    setModal(null);
    loadTasks();
    showToast('Task assigned successfully.', 'success');
  };

  const handleEdit = async (form) => {
    const id = selected._id || selected.id;
    await api.put(`/tasks/${id}`, toPayload(form));
    setModal(null);
    setSelected(null);
    loadTasks();
    showToast('Task updated.', 'success');
  };

  const handleDelete = async () => {
    const id = selected._id || selected.id;
    setDeleteLoading(true);
    try {
      await api.delete(`/tasks/${id}`);
      setModal(null);
      setSelected(null);
      loadTasks();
      showToast('Task deleted.', 'success');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Tasks grouped by assignee (for the picker dropdown counts) ────────────
  const tasksByAssignee = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      const a = t.assigned_to || t.assignedTo;
      const id = a?.id || a?._id || (typeof a === 'string' ? a : null);
      if (!id) return;
      if (!map[id]) map[id] = [];
      map[id].push(t);
    });
    return map;
  }, [tasks]);

  // Tasks of the currently selected assignee (filtered server-side by search).
  const assigneeTasks = useMemo(() => {
    if (!selectedAssigneeId) return [];
    return tasksByAssignee[selectedAssigneeId] || [];
  }, [tasksByAssignee, selectedAssigneeId]);

  const selectedEmployee = useMemo(
    () => employees.find((u) => (u.id || u._id) === selectedAssigneeId),
    [employees, selectedAssigneeId]
  );

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:      tasks.length,
    pending:    tasks.filter((t) => t.status === 'todo').length,
    inProgress: tasks.filter((t) => t.status === 'in_progress').length,
    completed:  tasks.filter((t) => t.status === 'completed').length,
  }), [tasks]);

  // ── Prepare initial form for edit ─────────────────────────────────────────────
  const editInitial = selected ? {
    title:       selected.title       || '',
    description: selected.description || '',
    // API returns assigned_to (snake_case object); fall back to camelCase for safety
    assignedTo:  selected.assigned_to?.id  || selected.assigned_to?._id
              || selected.assignedTo?.id   || selected.assignedTo?._id
              || (typeof (selected.assigned_to || selected.assignedTo) === 'string'
                    ? (selected.assigned_to || selected.assignedTo) : ''),
    // API returns due_date (snake_case); fall back to camelCase
    dueDate:     (selected.due_date || selected.dueDate || '').slice(0, 10),
    status:      selected.status      || 'todo',
  } : EMPTY_FORM;

  return (
    <div className="p-7 space-y-6 bg-[#F8FAFC]">

      <ToastHost toast={toast} clearToast={clearToast} />

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="label-meta mb-1">Task Management</p>
          <h1 className="heading-md text-[#1F2937]">Task Overview</h1>
          <p className="text-[#6B7280] text-sm mt-1">
            {stats.total} total · {stats.pending} pending · {stats.inProgress} in progress · {stats.completed} completed
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadTasks} className="btn flex items-center gap-2 text-[#6B7280]" style={{ padding: '0.6rem 1rem', fontSize: '13px' }}>
            <RiRefreshLine size={14} />
          </button>
          <button
            onClick={() => setModal('assign')}
            className="btn btn-primary flex items-center gap-2"
            style={{ padding: '0.6rem 1.2rem', fontSize: '13px' }}
          >
            <RiAddLine size={15} /> Assign Task
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Tasks',   value: stats.total,      gradient: 'from-[#0984e3] to-[#00b0ff]', shadow: 'rgba(9,132,227,0.25)',   icon: RiTaskLine },
          { label: 'Pending',       value: stats.pending,    gradient: 'from-[#6B7280] to-[#9CA3AF]',  shadow: 'rgba(107,114,128,0.2)',  icon: RiTimeLine },
          { label: 'In Progress',   value: stats.inProgress, gradient: 'from-[#f59e0b] to-[#fbbf24]',  shadow: 'rgba(245,158,11,0.25)',  icon: RiTimeLine },
          { label: 'Completed',     value: stats.completed,  gradient: 'from-[#10b981] to-[#34d399]',  shadow: 'rgba(16,185,129,0.25)',  icon: RiUserLine },
        ].map(({ label, value, gradient, shadow, icon: Icon }) => (
          <div key={label} className="bg-white border border-[#E5E7EB] rounded-2xl p-5 shadow-[0_4px_20px_rgba(15,23,42,0.06)]">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[#6B7280] text-sm">{label}</p>
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}
                style={{ boxShadow: `0 4px 12px ${shadow}` }}>
                <Icon size={16} className="text-white" />
              </div>
            </div>
            <p className="text-[#1F2937] text-3xl font-bold">{loading ? '—' : value}</p>
          </div>
        ))}
      </div>

      {/* Search + Assignee picker */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <RiSearchLine className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={17} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by task title…"
            className="input-field pl-10"
          />
        </div>

        {/* Assignee picker */}
        <div className="relative min-w-[260px]">
          <RiUserLine className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
          <select
            value={selectedAssigneeId}
            onChange={(e) => setSelectedAssigneeId(e.target.value)}
            className="input-field pl-10 pr-8 cursor-pointer w-full"
          >
            <option value="">Select an employee…</option>
            {employees.map((u) => {
              const id = u.id || u._id;
              const count = (tasksByAssignee[id] || []).length;
              return (
                <option key={id} value={id}>
                  {u.name || u.username || u.email_id}{count ? ` (${count})` : ''}
                </option>
              );
            })}
          </select>
        </div>

        {selectedAssigneeId && (
          <button
            onClick={() => setSelectedAssigneeId('')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E5E7EB] text-xs font-semibold text-[#6B7280] hover:text-red-500 hover:border-red-200 transition-colors cursor-pointer"
          >
            <RiCloseLine size={13} /> Clear
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>
      )}

      {/* Employee-wise task list */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#F3F4F6] rounded-xl animate-pulse" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-[#F3F4F6] rounded w-1/4 animate-pulse" />
                  <div className="h-3 bg-[#F3F4F6] rounded w-1/3 animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : !selectedAssigneeId ? (
        <div className="text-center py-16 bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)]">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#EFF6FF] to-[#FAF5FF] flex items-center justify-center mx-auto mb-3">
            <RiUserLine size={26} className="text-[#0984e3]" />
          </div>
          <p className="text-[#374151] font-semibold">Select an employee to view their Kanban board</p>
          <p className="text-[#9CA3AF] text-xs mt-1">
            {employees.length === 0
              ? 'No employees found. Add one in User Management.'
              : 'Pick a team member from the dropdown above to see their tasks.'}
          </p>
        </div>
      ) : (
        <div>
          {/* Selected assignee summary header */}
          <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] px-5 py-4 mb-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                   style={{ background: 'linear-gradient(135deg,#0984e3 0%,#d500f9 100%)' }}>
                {((selectedEmployee?.name || selectedEmployee?.username || selectedEmployee?.email_id || '?').match(/\b\w/g) || []).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="text-[#1F2937] text-sm font-bold">{selectedEmployee?.name || 'Employee'}</p>
                <p className="text-[#9CA3AF] text-xs">{selectedEmployee?.username || selectedEmployee?.email_id || ''}</p>
              </div>
            </div>
            <p className="text-[#6B7280] text-xs">
              <span className="font-semibold text-[#374151]">Tip:</span> drag a card from one column to another to update its status.
            </p>
          </div>

          {/* Kanban */}
          {assigneeTasks.length === 0 ? (
            <div className="text-center py-12 bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)]">
              <RiTaskLine size={32} className="text-[#E5E7EB] mx-auto mb-3" />
              <p className="text-[#9CA3AF] font-medium">No tasks assigned to this employee yet.</p>
              <button
                onClick={() => setModal('assign')}
                className="mt-3 text-[#0984e3] text-sm font-semibold hover:underline cursor-pointer"
              >
                + Assign a task
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {KANBAN_COLUMNS.map((col) => {
                const Icon     = col.icon;
                const colTasks = assigneeTasks.filter((t) => t.status === col.key);
                const isHover  = hoverColumn === col.key;
                return (
                  <div
                    key={col.key}
                    onDragOver={(e) => onDragOver(e, col.key)}
                    onDragLeave={(e) => onDragLeave(e, col.key)}
                    onDrop={(e) => onDrop(e, col.key)}
                    className={`bg-[#F8FAFC] border-2 rounded-2xl p-4 min-h-[420px] flex flex-col transition-all ${
                      isHover ? 'border-dashed shadow-[0_4px_20px_rgba(9,132,227,0.15)]' : 'border-[#E5E7EB]'
                    }`}
                    style={isHover ? { borderColor: col.accent, background: '#fff' } : {}}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon size={16} style={{ color: col.color }} />
                        <h3 className="text-[#1F2937] font-bold text-sm">{col.label}</h3>
                      </div>
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: `${col.color}1a`, color: col.color }}
                      >
                        {colTasks.length}
                      </span>
                    </div>

                    <div className="space-y-2.5 flex-1">
                      {colTasks.length === 0 ? (
                        <div className="text-center py-8 px-3 border-2 border-dashed border-[#E5E7EB] rounded-xl">
                          <p className="text-[#9CA3AF] text-xs">{isHover ? 'Drop here' : 'No tasks'}</p>
                        </div>
                      ) : (
                        colTasks.map((t) => {
                          const id    = t._id || t.id;
                          const isDrag = draggedId === id;
                          const isUpd  = updatingId === id;
                          const due    = t.due_date || t.dueDate;
                          return (
                            <div
                              key={id}
                              draggable={!isUpd}
                              onDragStart={(e) => onDragStart(e, id)}
                              onDragEnd={onDragEnd}
                              className={`group bg-white border border-[#E5E7EB] rounded-xl p-3 shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_16px_rgba(15,23,42,0.08)] transition-all ${
                                isUpd ? 'cursor-wait opacity-70' : 'cursor-grab active:cursor-grabbing'
                              } ${isDrag ? 'opacity-40 scale-95' : ''}`}
                            >
                              <div className="flex items-start gap-2">
                                <RiDraggable size={14} className="text-[#D1D5DB] group-hover:text-[#9CA3AF] mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-semibold ${
                                    col.key === 'completed' ? 'line-through text-[#9CA3AF]' : 'text-[#1F2937]'
                                  }`}>
                                    {t.title}
                                  </p>
                                  {t.description && (
                                    <p className="text-[#6B7280] text-xs leading-relaxed mt-1 line-clamp-2">
                                      {t.description}
                                    </p>
                                  )}
                                  {due && (
                                    <span className="mt-2 text-[11px] flex items-center gap-1 text-[#9CA3AF]">
                                      <RiCalendarLine size={11} />
                                      {formatDue(due)}
                                    </span>
                                  )}
                                </div>
                                {isUpd ? (
                                  <span className="w-3.5 h-3.5 border-2 border-[#0984e3] border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                ) : (
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                    <button
                                      onClick={() => { setSelected(t); setModal('edit'); }}
                                      className="p-1 text-[#9CA3AF] hover:text-[#0984e3] hover:bg-[rgba(9,132,227,0.08)] rounded transition-all cursor-pointer"
                                      aria-label="Edit"
                                    >
                                      <RiEditLine size={13} />
                                    </button>
                                    <button
                                      onClick={() => { setSelected(t); setModal('delete'); }}
                                      className="p-1 text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 rounded transition-all cursor-pointer"
                                      aria-label="Delete"
                                    >
                                      <RiDeleteBinLine size={13} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Assign Modal */}
      {modal === 'assign' && (
        <Modal title="Assign New Task" subtitle="Select an employee and fill in the task details" onClose={() => setModal(null)}>
          <TaskForm
            initial={EMPTY_FORM}
            employees={employees}
            onSave={handleAssign}
            onCancel={() => setModal(null)}
            isEdit={false}
          />
        </Modal>
      )}

      {/* Edit Modal */}
      {modal === 'edit' && selected && (
        <Modal title="Edit Task" subtitle={`"${selected.title}"`} onClose={() => { setModal(null); setSelected(null); }}>
          <TaskForm
            initial={editInitial}
            employees={employees}
            onSave={handleEdit}
            onCancel={() => { setModal(null); setSelected(null); }}
            isEdit
          />
        </Modal>
      )}

      {/* Delete Modal */}
      {modal === 'delete' && selected && (
        <Modal title="Delete Task" onClose={() => { setModal(null); setSelected(null); }}>
          <DeleteConfirm
            task={selected}
            onConfirm={handleDelete}
            onCancel={() => { setModal(null); setSelected(null); }}
            loading={deleteLoading}
          />
        </Modal>
      )}
    </div>
  );
}
